/* eslint-disable */
/**
 * NationalPartnerProvider — context for the National Partners (image leasability) flow.
 * Completely separate from ShopLocalProvider. These two flows must NOT share data.
 *
 * National Partners: user clicks a quick link (Best Buy, Home Depot, etc.),
 * takes a screenshot via Alt-T, and the image is sent to the leasability engine.
 *
 * Shop Local: user manually enters vendor/product details in the prefund form.
 */
import React, { useState, createContext, useContext, ReactNode, useEffect, useRef } from 'react';
import { getStoreConfigs } from '../../api/user';
import { ConfigDetails, GroupStoreConfigResponse, ParamConfigDetails, StoreConfigDetails } from '../../types/types';

// ─── Types ──────────────────────────────────────────────────────────────────

export type FundingState =
  | 'IDLE'
  | 'APPROVED'
  | 'DECLINED'
  | 'TIMED_OUT'
  | 'CONNECTION_ERROR';

export type CheckoutState = 'NONE' | 'IN_PROGRESS' | 'VERIFYING_PURCHASE' | 'PURCHASE_SUCCESSFUL' | 'CHECKOUT_FAILED';

export interface ResolvedProduct {
  itemNumber: string; // RMS item number
  weeklyRate: string;
  weeklyTerm: string;
  cashPrice: string;
  model: string;
  brand?: string;
  description?: string; // Product description/name from leasability
  costPerItem: string;
  modelNumber: string;
  quantity: string;
}

// ─── Data Interface ─────────────────────────────────────────────────────────

export interface NationalPartnerData {
  // Existing fields
  vendorName: string;
  vendorCode: string;
  captureId: number; // Increments on every vendor click — even same vendor
  isProcessing: boolean; // True when image is being evaluated

  // New — funding & checkout tracking
  fundingState: FundingState;
  spendLimitId: string;
  approvalLimitAmount: string;
  weeklyRate: string;
  totalCost: string;
  checkoutState: CheckoutState;
  resolvedProducts: ResolvedProduct[];
  storeNumber: string;
  vendorId: string | null;
  supplierName: string;
  supplierOrderNumber: string;
  externalLaunchUrl: string;  
  checkoutMessage: string;
  enablePricingProductClassifications: boolean;

  // Prefund SLA validation (RACV-308). No default/fallback amounts — null until
  // the config service returns a real value for this store.
  storePrefundMaxAmount: string | null;
  dmPrefundMaxAmount: string | null;
  configFetchFailed: boolean;
  slaApproved: boolean;
  checkoutFailedAcknowledged: boolean;
}

// ─── Mutators Interface ─────────────────────────────────────────────────────

export interface NationalPartnerMutators {
  setNationalPartnerVendor: (vendorName: string, vendorCode: string) => void;
  calcTotalPricesWithFudgeFactor: (originalPrice: number) => string;
  clearNationalPartnerVendor: () => void;
  setIsProcessing: (isProcessing: boolean) => void;

  // New setters — funding & checkout tracking
  setFundingState: (fundingState: FundingState) => void;
  setSpendLimitId: (spendLimitId: string) => void;
  setApprovalLimitAmount: (approvalLimitAmount: string) => void;
  setWeeklyRate: (weeklyRate: string) => void;
  setTotalCost: (totalCost: string) => void;
  setCheckoutState: (checkoutState: CheckoutState) => void;
  setResolvedProducts: (resolvedProducts: ResolvedProduct[]) => void;
  setStoreNumber: (storeNumber: string) => void;
  setVendorId: (vendorId: string | null) => void;
  setSupplierName: (supplierName: string) => void;
  setSupplierOrderNumber: (supplierOrderNumber: string) => void;
  setExternalLaunchUrl: (externalLaunchUrl: string) => void;
  resetExtendedState: () => void;
  setCheckoutMessage: (checkoutMessage: string) => void;

  // Prefund SLA validation (RACV-308)
  setSlaApproved: (approved: boolean) => void;
  setCheckoutFailedAcknowledged: (acknowledged: boolean) => void;
}

// ─── Default Values ─────────────────────────────────────────────────────────

const defaultData: NationalPartnerData = {
  vendorName: '',
  vendorCode: '',
  captureId: 0,
  isProcessing: false,
  fundingState: 'IDLE',
  spendLimitId: '',
  approvalLimitAmount: '',
  weeklyRate: '',
  totalCost: '',
  checkoutState: 'NONE',
  resolvedProducts: [],
  storeNumber: '',
  vendorId: null,
  supplierName: '',
  supplierOrderNumber: '',
  externalLaunchUrl: '',  
  checkoutMessage: '',
  enablePricingProductClassifications: false,
  storePrefundMaxAmount: null,
  dmPrefundMaxAmount: null,
  configFetchFailed: false,
  slaApproved: false,
  checkoutFailedAcknowledged: false,
};

const defaultMutators: NationalPartnerMutators = {
  setNationalPartnerVendor: () => {},
  calcTotalPricesWithFudgeFactor: () => {return ''},
  clearNationalPartnerVendor: () => {},
  setIsProcessing: () => {},
  setFundingState: () => {},
  setSpendLimitId: () => {},
  setApprovalLimitAmount: () => {},
  setWeeklyRate: () => {},
  setTotalCost: () => {},
  setCheckoutState: () => {},
  setResolvedProducts: () => {},
  setStoreNumber: () => {},
  setVendorId: () => {},
  setSupplierName: () => {},
  setSupplierOrderNumber: () => {},
  setExternalLaunchUrl: () => {},
  resetExtendedState: () => {},
  setCheckoutMessage: () => {},
  setSlaApproved: () => {},
  setCheckoutFailedAcknowledged: () => {},
};

// ─── Contexts ───────────────────────────────────────────────────────────────

const NationalPartnerDataContext = createContext<NationalPartnerData>(defaultData);
const NationalPartnerMutatorContext = createContext<NationalPartnerMutators>(defaultMutators);

// ─── Provider ───────────────────────────────────────────────────────────────

export interface NationalPartnerProviderProps {
  children: ReactNode;
}

export const NationalPartnerProvider = ({ children }: NationalPartnerProviderProps) => {
  // Existing state
  const [vendorName, setVendorName] = useState<string>('');
  const [vendorCode, setVendorCode] = useState<string>('');
  const [captureId, setCaptureId] = useState<number>(0);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // the fudge factor to use for the amount of Funding
  const varianceThreshold = useRef<string>('12');

  // Flag: use new RACAPI classifications (POST /classification) vs old enterprise (GET /classifications/processed)
  const [enablePricingProductClassifications, setEnablePricingProductClassifications] = useState<boolean>(false);
  

  // New state — funding & checkout tracking
  const [fundingState, setFundingState] = useState<FundingState>('IDLE');
  const [spendLimitId, setSpendLimitId] = useState<string>('');
  const [approvalLimitAmount, setApprovalLimitAmount] = useState<string>('');
  const [weeklyRate, setWeeklyRate] = useState<string>('');
  const [totalCost, setTotalCost] = useState<string>('');
  const [checkoutState, setCheckoutState] = useState<CheckoutState>('NONE');
  const [resolvedProducts, setResolvedProducts] = useState<ResolvedProduct[]>([]);
  const [storeNumber, setStoreNumber] = useState<string>('');
  const [vendorId, setVendorId] = useState<string | null>(null);
  const [supplierName, setSupplierName] = useState<string>('');
  const [supplierOrderNumber, setSupplierOrderNumber] = useState<string>('');
  const [externalLaunchUrl, setExternalLaunchUrl] = useState<string>('');
  const [checkoutMessage, setCheckoutMessage] = useState<string>('');

  // Prefund SLA validation state (RACV-308)
  const [storePrefundMaxAmount, setStorePrefundMaxAmount] = useState<string | null>(null);
  const [dmPrefundMaxAmount, setDmPrefundMaxAmount] = useState<string | null>(null);
  const [configFetchFailed, setConfigFetchFailed] = useState<boolean>(false);
  const [slaApproved, setSlaApproved] = useState<boolean>(false);
  const [checkoutFailedAcknowledged, setCheckoutFailedAcknowledged] = useState<boolean>(false);
  useEffect(() => {
    setLocalSourcingConfigs();
  }, []);


  const setPricingClassificationConfig = ( resp : any ) => {
    setEnablePricingProductClassifications(false);
    const storeConfigs : StoreConfigDetails[] = resp.data[0].configDetails;
    storeConfigs.forEach( (oneStoreConfig) => {
      if ( oneStoreConfig.paramKeyName == "EnablePricingProductClassifications" ) {
        oneStoreConfig.paramConfigs[0]?.configValue == '1' ?
          setEnablePricingProductClassifications(true) 
          :
          setEnablePricingProductClassifications(false);
      }
    });

  }

  const setFudgeFactor = ( resp : any ) => {
    const storeConfigs : StoreConfigDetails[] = resp.data[0].configDetails;
    // note:  ONLY calling this for ONE STORE !!!!! 
    storeConfigs.forEach( (oneStoreConfig) => {

      const paramConfigs: ParamConfigDetails[] = oneStoreConfig.paramConfigs;

      paramConfigs.forEach( (oneParamConfig)  => {
        if ( oneParamConfig.configName === 'VarianceThreshold') {
          varianceThreshold.current = oneParamConfig.configValue;
        } else if (oneParamConfig.configName === 'StorePrefundMaxAmount') {
          console.log('[SLA] Found StorePrefundMaxAmount:', oneParamConfig.configValue);
          setStorePrefundMaxAmount(oneParamConfig.configValue);
        } else if (oneParamConfig.configName === 'DMPrefundMaxAmount') {
          console.log('[SLA] Found DMPrefundMaxAmount:', oneParamConfig.configValue);
          setDmPrefundMaxAmount(oneParamConfig.configValue);
        } 
      });

    });    
  };

  const calcTotalPricesWithFudgeFactor = (originalPrice): string => {
    let percentToAdd = 1.1;
    if (varianceThreshold.current) {
      percentToAdd = 1 + Number(varianceThreshold.current) / 100;
      if (Number.isNaN(percentToAdd)) {
        percentToAdd = 1.1;
      }
    }
    return (percentToAdd * originalPrice).toFixed(2);
  };

  const setLocalSourcingConfigs = async () => {

    const storeToUse = window.sessionStorage.getItem('storeNumber');
    await getStoreConfigs( {                       
        storeNumbers: [storeToUse],
        paramKeyNames: ['LOCAL_SOURCING_CONFIG','EnablePricingProductClassifications'],
      }).then((response) => {
        console.log('get-store-profile called to set variance on mount');
        setFudgeFactor(response);
        setPricingClassificationConfig(response);
      })
      .catch(() => {
        console.log('ERROR detected calling get-store-profile!! ');
        setConfigFetchFailed(true);
      });

  };

  // Existing actions
  const setNationalPartnerVendor = (name: string, code: string) => {
    setVendorName(name);
    setVendorCode(code);
    setCaptureId((prev) => prev + 1); // Always increment — even same vendor
  };

  const clearNationalPartnerVendor = () => {
    setVendorName('');
    setVendorCode('');
  };

  // New action — reset all extended state to initial values
  // NOTE: externalLaunchUrl is NOT reset here because it's tied to vendorCode,
  // which has its own useEffect. Resetting it here causes the "Continue Checkout"
  // button to break on recapture (same vendor clicked again).
  const resetExtendedState = () => {
    setFundingState('IDLE');
    setSpendLimitId('');
    setApprovalLimitAmount('');
    setWeeklyRate('');
    setTotalCost('');
    setCheckoutState('NONE');
    setResolvedProducts([]);
    setStoreNumber('');
    setVendorId(null);
    setSupplierName('');
    setSupplierOrderNumber('');
    setSlaApproved(false);
    setCheckoutFailedAcknowledged(false);
  };

  const data: NationalPartnerData = {
    vendorName,
    vendorCode,
    captureId,
    isProcessing,
    fundingState,
    spendLimitId,
    approvalLimitAmount,
    weeklyRate,
    totalCost,
    checkoutState,
    resolvedProducts,
    storeNumber,
    vendorId,
    supplierName,
    supplierOrderNumber,
    externalLaunchUrl,
    checkoutMessage,
    enablePricingProductClassifications: enablePricingProductClassifications,
    storePrefundMaxAmount,
    dmPrefundMaxAmount,
    configFetchFailed,
    slaApproved,
    checkoutFailedAcknowledged,
  };

  const mutators: NationalPartnerMutators = {
    setNationalPartnerVendor,
    calcTotalPricesWithFudgeFactor,
    clearNationalPartnerVendor,
    setIsProcessing,
    setFundingState,
    setSpendLimitId,
    setApprovalLimitAmount,
    setWeeklyRate,
    setTotalCost,
    setCheckoutState,
    setResolvedProducts,
    setStoreNumber,
    setVendorId,
    setSupplierName,
    setSupplierOrderNumber,
    setExternalLaunchUrl,
    resetExtendedState,
    setCheckoutMessage,
    setSlaApproved,
    setCheckoutFailedAcknowledged,
  };

  return (
    <NationalPartnerDataContext.Provider value={data}>
      <NationalPartnerMutatorContext.Provider value={mutators}>
        {children}
      </NationalPartnerMutatorContext.Provider>
    </NationalPartnerDataContext.Provider>
  );
};

// ─── Hooks ──────────────────────────────────────────────────────────────────

export const useNationalPartnerData = () => useContext(NationalPartnerDataContext);
export const useNationalPartnerActions = () => useContext(NationalPartnerMutatorContext);
